import { Timestamp, TranslateFunction } from '@griply/types';
import {
  differenceInCalendarDays,
  differenceInHours,
  format,
  isAfter,
  isSameYear,
} from 'date-fns';

export type GetTimeLeftLabelOptions = {
  today: Timestamp;
  t: TranslateFunction;
  short?: boolean;
  midShort?: boolean;
  isCompleted?: boolean;
};

export type TimeLeftLabel = {
  label: string;
  isOverdue: boolean;
  isLastWeek: boolean;
  isCompleted: boolean;
};

const resolveKey = (
  base: string,
  { short, midShort }: { short?: boolean; midShort?: boolean },
) => {
  if (short) {
    return `${base}.short.title`;
  }
  if (midShort) {
    return `${base}.mid-short.title`;
  }
  return `${base}.title`;
};

const resolveOverdueKey = ({
  short,
  midShort,
}: {
  short?: boolean;
  midShort?: boolean;
}) => {
  if (short) {
    return 'general.labels.days-ago.short';
  }
  if (midShort) {
    return 'general.labels.days-ago.mid-short';
  }
  return 'general.labels.days-ago';
};

// Mobile runs on Hermes which forces i18next into `compatibilityJSON: 'v3'`
// mode, disabling automatic `_one` / `_other` plural resolution. We pick the
// suffix manually so the same translation keys work on both platforms.
const withPluralSuffix = (key: string, count: number) =>
  count === 1 ? `${key}_one` : `${key}_other`;

export const getTimeLeftLabel = (
  date: Timestamp,
  { today, t, short, midShort, isCompleted }: GetTimeLeftLabelOptions,
): TimeLeftLabel => {
  if (isCompleted) {
    const dateFormat = isSameYear(date, today) ? 'MMM d' : "MMM d, ''yy";
    return {
      label: format(date, dateFormat),
      isOverdue: false,
      isLastWeek: false,
      isCompleted: true,
    };
  }

  const isOverdue = isAfter(today, date);
  const hoursLeft = differenceInHours(date, today);
  const daysLeft = isOverdue
    ? differenceInCalendarDays(date, today)
    : Math.ceil(hoursLeft / 24);
  const isLastWeek = daysLeft <= 7;

  if (isOverdue) {
    const amount = short ? daysLeft : Math.abs(daysLeft);
    return {
      label: t(
        withPluralSuffix(resolveOverdueKey({ short, midShort }), amount),
        { amount, count: amount },
      ),
      isOverdue,
      isLastWeek,
      isCompleted: false,
    };
  }

  if (daysLeft >= 365) {
    const yearsLeftCount = daysLeft / 365;
    const yearsLeft =
      yearsLeftCount % 1 === 0
        ? Math.floor(yearsLeftCount)
        : Number(yearsLeftCount.toFixed(1));
    return {
      label: t(
        withPluralSuffix(
          resolveKey('general.labels.years-left', { short, midShort }),
          yearsLeftCount,
        ),
        { amount: yearsLeft, count: yearsLeftCount },
      ),
      isOverdue,
      isLastWeek,
      isCompleted: false,
    };
  }

  if (daysLeft >= 30) {
    const monthsLeft = Math.floor(daysLeft / 30);
    return {
      label: t(
        withPluralSuffix(
          resolveKey('general.labels.months-left', { short, midShort }),
          monthsLeft,
        ),
        { amount: monthsLeft, count: monthsLeft },
      ),
      isOverdue,
      isLastWeek,
      isCompleted: false,
    };
  }

  if (daysLeft >= 15) {
    const weeksLeft = Math.floor(daysLeft / 7);
    return {
      label: t(
        withPluralSuffix(
          resolveKey('general.labels.weeks-left', { short, midShort }),
          weeksLeft,
        ),
        { amount: weeksLeft, count: weeksLeft },
      ),
      isOverdue,
      isLastWeek,
      isCompleted: false,
    };
  }

  return {
    label: t(
      withPluralSuffix(
        resolveKey('general.labels.days-left', { short, midShort }),
        daysLeft,
      ),
      { amount: daysLeft, count: daysLeft },
    ),
    isOverdue,
    isLastWeek,
    isCompleted: false,
  };
};
